import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import Alert from "@mui/material/Alert/Alert";
import Card from "@mui/material/Card/Card";
import CardContent from "@mui/material/CardContent/CardContent";
import FormControl from "@mui/material/FormControl/FormControl";
import FormHelperText from "@mui/material/FormHelperText/FormHelperText";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select/Select";
import Stack from "@mui/material/Stack/Stack";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker/DateTimePicker";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Address } from "ton-core";
import { useSendFBAnalyticsEvent } from "../hooks/analytics";
import {
  AuctionParams,
  ProposalActions,
  useCreateProposal,
  useDaoData,
} from "../libs/daoService";
import {
  MintTokensAction,
  validateMintJettonActionParams,
} from "./fields/MintJettonFields";
import {
  NoneActionFields,
  NoneActionProps,
  RequiredError,
  validateNoneActionParams,
} from "./fields/NoneActionFields";
import {
  SendJettonAction,
  SendJettonActionProps,
  validateSendJettonActionParams,
} from "./fields/SendJettonFields";
import {
  SendNftAction,
  SendNftActionProps,
  validateSendNftActionParams,
} from "./fields/SendNftFields";
import {
  SendTonAction,
  SendTonActionProps,
  validateSendTonActionParams,
} from "./fields/SendTonFields";
import {
  UpdateContentAction,
  UpdateContentActionProps,
  validateUpdateContentActionParams,
} from "./fields/UpdateContentFields";
import {
  UpdateQuorumAction,
  UpdateQuorumActionProps,
  validateUpdateQuorumActionParams,
} from "./fields/UpdateQuorumFields";

export interface MintTokensActionProps {
  kind: ProposalActions.mintTokens;
  address: string;
  jettonAmount: string;
}

export const seeIfValidAddress = (value?: string): boolean => {
  if (!value) return false;
  try {
    Address.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

const useActionValidation = (action: Partial<AuctionParams>) => {
  const { address } = useParams();

  return useQuery<Partial<AuctionParams>>(
    [address, "action", action],
    async () => {
      switch (action.kind) {
        case ProposalActions.none:
          return await validateNoneActionParams(action);
        case ProposalActions.mintTokens:
          return await validateMintJettonActionParams(action);
        case ProposalActions.sendTon:
          return await validateSendTonActionParams(action);
        case ProposalActions.sendJetton:
          return await validateSendJettonActionParams(action);
        case ProposalActions.sendNft:
          return await validateSendNftActionParams(action);

        case ProposalActions.changeContent:
          return await validateUpdateContentActionParams(action);
        case ProposalActions.changeQuorum:
          return await validateUpdateQuorumActionParams(action);
        default:
          return action;
      }
    }
  );
};

export const NewProposal = () => {
  const navigate = useNavigate();

  const sendFBAnalyticsEvent = useSendFBAnalyticsEvent();

  const { data: dao } = useDaoData();

  const { mutateAsync, error, isLoading } = useCreateProposal();
  const [expiration, setExpiration] = useState<dayjs.Dayjs | null>(null);

  const [submitted, setSubmitted] = useState(false);
  const [action, setAction] = useState<Partial<AuctionParams>>({
    kind: ProposalActions.none,
  });

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      if (event.target.value === ProposalActions.changeQuorum) {
        setAction({
          kind: event.target.value,
          quorum: dao
            ? `${dao.quorumNominator}/${dao.quorumDenominator}`
            : "1/2",
        } as Partial<UpdateQuorumActionProps>);
      } else if (event.target.value === ProposalActions.changeContent) {
        setAction({
          kind: event.target.value,
          daoDescription: dao?.daoContent.description,
          daoImage: dao?.daoContent.image,
          daoName: dao?.daoContent.name,
        } as Partial<UpdateContentActionProps>);
      } else {
        setAction({ kind: event.target.value } as Partial<AuctionParams>);
      }
    },
    [setAction, dao]
  );

  const { data: actionValidation, isFetching } = useActionValidation(action);

  const setParams = useCallback(
    (value: Partial<AuctionParams>) => {
      setAction((action) => ({ ...action, ...value }));
    },
    [setAction]
  );

  const expirationError = useMemo(() => {
    if (!expiration) {
      return RequiredError;
    }

    const soon = new Date();
    soon.setMinutes(soon.getMinutes() + 3);

    if (expiration.toDate().getTime() < soon.getTime()) {
      return `Min expiration date is ${soon.toLocaleString()}`;
    }

    const max_voting_duration = 2592000 * 1000; // 30 days
    const max = new Date(Date.now() + max_voting_duration);

    if (expiration.toDate().getTime() > max.getTime()) {
      return `Max expiration date is ${max.toLocaleString()}`;
    }

    return null;
  }, [expiration]);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setSubmitted(true);

    if (
      !expiration ||
      expirationError != null ||
      !dao?.daoContent.image ||
      !actionValidation ||
      Object.keys(actionValidation).length > 1
    ) {
      return;
    }

    sendFBAnalyticsEvent("create_proposal");
    await mutateAsync({
      expirationDate: expiration.toDate(),
      image: dao.daoContent.image,
      action: action as AuctionParams,
    });

    navigate("../");
  };

  const disabled = isLoading;

  return (
    <Card sx={{ my: 2 }}>
      <CardContent>
        <FormControl
          component="form"
          onSubmit={onSubmit}
          disabled={disabled}
          fullWidth
        >
          <Stack gap={2}>
            <div>
              <FormHelperText sx={{ mx: "12px" }}>
                Expiration Date Time
              </FormHelperText>
              <DateTimePicker
                value={expiration}
                onChange={(date) => setExpiration(date)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    disabled,
                    error: submitted && expirationError != null,
                    helperText:
                      submitted && expirationError != null
                        ? expirationError
                        : undefined,
                  },
                }}
              />
            </div>

            <div>
              <FormHelperText sx={{ mx: "12px" }}>Action</FormHelperText>
              <Select
                fullWidth
                placeholder="Action"
                disabled={disabled}
                value={action.kind}
                onChange={handleChange}
              >
                <MenuItem value={ProposalActions.none}>None</MenuItem>
                <MenuItem value={ProposalActions.sendTon}>Send TON</MenuItem>
                <MenuItem value={ProposalActions.sendJetton}>
                  Send Jetton
                </MenuItem>

                <MenuItem value={ProposalActions.sendNft}>Send NFT</MenuItem>

                <MenuItem value={ProposalActions.mintTokens}>
                  Mint DAO Tokens
                </MenuItem>
                <MenuItem value={ProposalActions.changeContent}>
                  Update DAO Content
                </MenuItem>
                <MenuItem value={ProposalActions.changeQuorum}>
                  Update DAO Quorum
                </MenuItem>
              </Select>
            </div>

            {(() => {
              switch (action.kind) {
                case ProposalActions.none:
                  return (
                    <NoneActionFields
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={actionValidation as Partial<NoneActionProps>}
                    />
                  );
                case ProposalActions.mintTokens:
                  return (
                    <MintTokensAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<MintTokensActionProps>
                      }
                    />
                  );
                case ProposalActions.sendTon:
                  return (
                    <SendTonAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<SendTonActionProps>
                      }
                    />
                  );
                case ProposalActions.sendJetton:
                  return (
                    <SendJettonAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<SendJettonActionProps>
                      }
                    />
                  );

                case ProposalActions.sendNft:
                  return (
                    <SendNftAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<SendNftActionProps>
                      }
                    />
                  );

                case ProposalActions.changeContent:
                  return (
                    <UpdateContentAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<UpdateContentActionProps>
                      }
                    />
                  );

                case ProposalActions.changeQuorum:
                  return (
                    <UpdateQuorumAction
                      params={action}
                      setParams={setParams}
                      disabled={disabled}
                      submitted={submitted}
                      validation={
                        actionValidation as Partial<UpdateQuorumActionProps>
                      }
                    />
                  );

                default:
                  return <></>;
              }
            })()}
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              loading={isLoading || isFetching}
              disabled={disabled}
            >
              Submit
            </LoadingButton>
            {error && <Alert severity="error">{error.message}</Alert>}
          </Stack>
        </FormControl>
      </CardContent>
    </Card>
  );
};
